import { createContext, useContext, useEffect, useState } from "react";

const LanguageContext = createContext(null);

const TRANSLATIONS = {
  en: {
    tagline: "Fresh Done Right",
    heroTitle: "A menu built on freshness.",
    heroSub: "Explore ZED's seasonal selection — from slow-brewed espressos to garden-fresh salads. Tap any item to see full nutrition.",
    featuredWeek: "Featured this week",
    searchPlaceholder: "Search the menu…",
    categories: {
      all: "All",
      hot_drinks: "Hot Drinks",
      cold_drinks: "Cold Drinks",
      meals: "Meals",
      salads: "Salads",
      snacks: "Snacks",
      fresh_drinks: "Fresh Drinks",
    },
    currency: "SAR",
    calories: "Calories",
    kcal: "kcal",
    price: "Price",
    close: "Close",
    empty: "No items in this category yet.",
    footer: "© ZED — Fresh Done Right",
    admin: "Admin",
    protein: "Protein",
    carbs: "Carbs",
    fat: "Fat",
    nutrition: "Nutrition",
    grams: "g",
    // admin
    adminTitle: "ZED Admin",
    email: "Email",
    password: "Password",
    signIn: "Sign in",
    logout: "Logout",
    addItem: "Add item",
    editItem: "Edit item",
    deleteItem: "Delete",
    save: "Save",
    cancel: "Cancel",
    nameEn: "Name (English)",
    nameAr: "Name (Arabic)",
    descEn: "Description (English)",
    descAr: "Description (Arabic)",
    category: "Category",
    imageUrl: "Image URL",
    imageOr: "or",
    uploadImage: "Upload image",
    uploading: "Uploading…",
    image: "Image",
    name: "Name",
    actions: "Actions",
    duplicate: "Duplicate",
    nameEnShort: "Name (EN)",
    nameArShort: "Name (AR)",
    calShort: "Cal",
    protShort: "Prot",
    carbShort: "Carb",
    fatShort: "Fat",
    addRow: "Add",
    noVariants: "No rows yet — click Add.",
    sizesLabel: "Sizes",
    milkOptionsLabel: "Milk options",
    chooseSize: "Choose size",
    chooseMilk: "Choose milk",
    from: "From",
    featured: "Featured",
    active: "Active",
    activeHint: "Turn off to hide this item from the menu temporarily.",
    itemShown: "Item is now visible on the menu",
    itemHidden: "Item hidden from the menu",
    views: "Views",
    itemAdded: "Item added",
    itemUpdated: "Item updated",
    itemDeleted: "Item deleted",
    loginFailed: "Invalid credentials",
    confirmDelete: "Delete this item?",
    tabItems: "Items",
    tabAnalytics: "Analytics",
    tabCategories: "Categories",
    tabPromotions: "Promotions",
    addPromotion: "Add promotion",
    promoImage: "Banner image",
    promoImageHint: "Use a wide/landscape image (shows inside the Featured carousel).",
    promoImageRequired: "Please add a banner image first.",
    promoTitleEn: "Title (English) — optional",
    promoTitleAr: "Title (Arabic) — optional",
    promoSubtitleEn: "Subtitle (English) — optional",
    promoSubtitleAr: "Subtitle (Arabic) — optional",
    noPromotions: "No promotions yet.",
    totalViews: "Total views",
    topItems: "Most viewed",
    noViewsYet: "No views tracked yet.",
    // categories
    addCategory: "Add category",
    editCategory: "Edit category",
    categoryKey: "Key (slug)",
    categoryKeyHint: "Leave empty to auto-generate from name",
    reorder: "Drag to reorder",
    active: "Active",
    inactive: "Inactive",
    moveUp: "Move up",
    moveDown: "Move down",
    itemsInCategory: "items",
    categoryHasItems: "Cannot delete — items still use this category.",
    categoryDeleted: "Category deleted",
    categorySaved: "Category saved",
    all: "All",
    // rbac & users & settings
    tabSettings: "Settings",
    tabUsers: "Users",
    superAdmin: "Super Admin",
    editor: "Editor",
    role: "Role",
    userName: "Name",
    status: "Status",
    lastLogin: "Last login",
    addUser: "Add user",
    editUser: "Edit user",
    activate: "Activate",
    deactivate: "Deactivate",
    // settings labels
    brandIdentity: "Brand identity",
    brandNameEn: "Brand name (EN)",
    brandNameAr: "Brand name (AR)",
    taglineEn: "Tagline (EN)",
    taglineAr: "Tagline (AR)",
    descriptionEn: "Description (EN)",
    descriptionAr: "Description (AR)",
    logo: "Logo",
    logoHint: "Upload a square image. Shown in the header, footer and browser tab.",
    contact: "Contact",
    contactEmail: "Contact email",
    contactPhone: "Contact phone",
    addressEn: "Address (EN)",
    addressAr: "Address (AR)",
    social: "Social",
    footerNoteEn: "Footer note (EN)",
    footerNoteAr: "Footer note (AR)",
    settingsSaved: "Settings saved",
    accessDenied: "Access denied",
    // login/rate-limit
    tooManyAttempts: "Too many attempts. Please wait a minute.",
    retryIn: "Try again in",
    seconds: "s",
    // password reset
    forgotPassword: "Forgot password?",
    resetPasswordTitle: "Reset your password",
    resetPasswordHint: "Enter your account email — we'll send a reset link (in dev, the link is logged to the backend console).",
    sendResetLink: "Send reset link",
    resetLinkSent: "If the email is registered, a reset link has been sent.",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    passwordMismatch: "Passwords do not match",
    passwordReset: "Password reset. You can sign in now.",
    invalidResetToken: "Invalid or expired token",
    backToLogin: "Back to sign in",
    // audit
    tabAudit: "Audit",
    auditActor: "Actor",
    auditAction: "Action",
    auditResource: "Resource",
    auditSummary: "Details",
    auditWhen: "When",
    auditIp: "IP",
    noAuditYet: "No admin actions recorded yet.",
    filterAll: "All",
    action_create: "Created",
    action_update: "Updated",
    action_delete: "Deleted",
    action_reorder: "Reordered",
    action_upload: "Uploaded",
    action_request_reset: "Reset requested",
    action_reset_password: "Password reset",
    resource_menu_item: "Menu item",
    resource_category: "Category",
    resource_user: "User",
    resource_settings: "Settings",
    resource_file: "File",
    // branch / order-type
    branchSelectTitle: "Which branch are you at?",
    branchSelectHint: "So we can show the right menu, tell us the branch type. You can switch anytime from the top bar.",
    branchFull: "Full branch",
    branchFullHint: "Kitchen items available — burgers, meals, salads and more.",
    branchDriveThru: "Drive-thru",
    branchDriveThruHint: "Grab-and-go menu — drinks, snacks and desserts only.",
    branchChangeLabel: "Branch",
    branchChange: "Change branch",
    kitchenRequired: "Kitchen required",
    kitchenRequiredHint: "Hidden on Drive-thru branches (needs a full kitchen).",
    driveThruBadge: "Drive-thru menu",
    fullBadge: "Full menu",
    tagKitchen: "Kitchen",
    // invites / email
    sendInviteEmail: "Send invite email",
    sendInviteHint: "We'll email a one-time link to set their password. If email isn't configured yet, the link is logged in the backend console.",
    passwordOrInviteHint: "leave empty to send an invite email instead",
    leaveBlankToInvite: "(blank = email invite)",
    inviteSent: "Invite email sent",
    inviteQueuedNoKey: "User created — configure email on the server (SMTP_* or RESEND_API_KEY) to deliver the invite email",
    // QR posters
    qrPosterTitle: "Branch QR posters",
    qrPosterHint: "Print these at each branch so customers land on the right menu.",
    qrDownloadFull: "Download full-branch poster",
    qrDownloadDrive: "Download drive-thru poster",
    qrScanCaption: "Scan for our menu",
  },
  ar: {
    tagline: "طازج، بإتقان",
    heroTitle: "قائمة مبنية على الطزاجة.",
    heroSub: "استكشف مجموعة زد الموسمية — من الإسبريسو المُحضّر ببطء إلى السلطات الطازجة. اضغط على أي صنف لعرض التفاصيل الغذائية.",
    featuredWeek: "مميز هذا الأسبوع",
    searchPlaceholder: "ابحث في القائمة…",
    categories: {
      all: "الكل",
      hot_drinks: "مشروبات ساخنة",
      cold_drinks: "مشروبات باردة",
      meals: "وجبات",
      salads: "سلطات",
      snacks: "وجبات خفيفة",
      fresh_drinks: "عصائر طازجة",
    },
    currency: "ر.س",
    calories: "السعرات",
    kcal: "سعرة حرارية",
    price: "السعر",
    close: "إغلاق",
    empty: "لا توجد أصناف في هذه الفئة بعد.",
    footer: "© زد — طازج، بإتقان",
    admin: "المشرف",
    protein: "بروتين",
    carbs: "كربوهيدرات",
    fat: "دهون",
    nutrition: "التفاصيل الغذائية",
    grams: "جم",
    adminTitle: "إدارة زد",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    signIn: "تسجيل الدخول",
    logout: "خروج",
    addItem: "إضافة صنف",
    editItem: "تعديل الصنف",
    deleteItem: "حذف",
    save: "حفظ",
    cancel: "إلغاء",
    nameEn: "الاسم (إنجليزي)",
    nameAr: "الاسم (عربي)",
    descEn: "الوصف (إنجليزي)",
    descAr: "الوصف (عربي)",
    category: "الفئة",
    imageUrl: "رابط الصورة",
    imageOr: "أو",
    uploadImage: "رفع صورة",
    uploading: "جاري الرفع…",
    image: "صورة",
    name: "الاسم",
    actions: "إجراءات",
    duplicate: "تكرار",
    nameEnShort: "الاسم (إنجليزي)",
    nameArShort: "الاسم (عربي)",
    calShort: "سعرات",
    protShort: "بروتين",
    carbShort: "كارب",
    fatShort: "دهون",
    addRow: "إضافة",
    noVariants: "لا توجد صفوف بعد — اضغط إضافة.",
    sizesLabel: "الأحجام",
    milkOptionsLabel: "خيارات الحليب",
    chooseSize: "اختر الحجم",
    chooseMilk: "اختر الحليب",
    from: "يبدأ من",
    featured: "مميز",
    active: "مفعّل",
    activeHint: "أوقفه لإخفاء المنتج من المنيو مؤقتاً.",
    itemShown: "المنتج ظاهر الآن في المنيو",
    itemHidden: "تم إخفاء المنتج من المنيو",
    views: "المشاهدات",
    itemAdded: "تمت الإضافة",
    itemUpdated: "تم التحديث",
    itemDeleted: "تم الحذف",
    loginFailed: "بيانات غير صحيحة",
    confirmDelete: "هل تريد حذف هذا الصنف؟",
    tabItems: "الأصناف",
    tabAnalytics: "الإحصائيات",
    tabCategories: "الفئات",
    tabPromotions: "الإعلانات",
    addPromotion: "إضافة إعلان",
    promoImage: "صورة البانر",
    promoImageHint: "استخدم صورة عريضة/أفقية (تظهر داخل كاروسيل Featured).",
    promoImageRequired: "من فضلك أضف صورة البانر أولًا.",
    promoTitleEn: "العنوان (إنجليزي) — اختياري",
    promoTitleAr: "العنوان (عربي) — اختياري",
    promoSubtitleEn: "النص الفرعي (إنجليزي) — اختياري",
    promoSubtitleAr: "النص الفرعي (عربي) — اختياري",
    noPromotions: "لا توجد إعلانات بعد.",
    totalViews: "إجمالي المشاهدات",
    topItems: "الأكثر مشاهدة",
    noViewsYet: "لا توجد مشاهدات بعد.",
    addCategory: "إضافة فئة",
    editCategory: "تعديل الفئة",
    categoryKey: "المعرّف",
    categoryKeyHint: "اتركه فارغًا للتوليد التلقائي",
    reorder: "اسحب لإعادة الترتيب",
    active: "نشط",
    inactive: "غير نشط",
    moveUp: "أعلى",
    moveDown: "أسفل",
    itemsInCategory: "أصناف",
    categoryHasItems: "لا يمكن الحذف — توجد أصناف تستخدم هذه الفئة.",
    categoryDeleted: "تم حذف الفئة",
    categorySaved: "تم الحفظ",
    all: "الكل",
    tabSettings: "الإعدادات",
    tabUsers: "المستخدمون",
    superAdmin: "مشرف عام",
    editor: "محرر",
    role: "الدور",
    userName: "الاسم",
    status: "الحالة",
    lastLogin: "آخر تسجيل",
    addUser: "إضافة مستخدم",
    editUser: "تعديل مستخدم",
    activate: "تفعيل",
    deactivate: "إلغاء التفعيل",
    brandIdentity: "الهوية",
    brandNameEn: "اسم العلامة (إنجليزي)",
    brandNameAr: "اسم العلامة (عربي)",
    taglineEn: "الشعار (إنجليزي)",
    taglineAr: "الشعار (عربي)",
    descriptionEn: "الوصف (إنجليزي)",
    descriptionAr: "الوصف (عربي)",
    logo: "الشعار",
    logoHint: "ارفع صورة مربعة تظهر في الترويسة والتذييل.",
    contact: "التواصل",
    contactEmail: "البريد الإلكتروني",
    contactPhone: "الهاتف",
    addressEn: "العنوان (إنجليزي)",
    addressAr: "العنوان (عربي)",
    social: "وسائل التواصل",
    footerNoteEn: "نص التذييل (إنجليزي)",
    footerNoteAr: "نص التذييل (عربي)",
    settingsSaved: "تم الحفظ",
    accessDenied: "الوصول مرفوض",
    tooManyAttempts: "محاولات كثيرة، الرجاء الانتظار دقيقة.",
    retryIn: "أعد المحاولة خلال",
    seconds: "ث",
    forgotPassword: "نسيت كلمة المرور؟",
    resetPasswordTitle: "إعادة تعيين كلمة المرور",
    resetPasswordHint: "أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين.",
    sendResetLink: "أرسل الرابط",
    resetLinkSent: "إذا كان البريد مسجّلًا، تم إرسال رابط إعادة التعيين.",
    newPassword: "كلمة المرور الجديدة",
    confirmPassword: "تأكيد كلمة المرور",
    passwordMismatch: "كلمتا المرور غير متطابقتين",
    passwordReset: "تم إعادة تعيين كلمة المرور.",
    invalidResetToken: "رمز غير صالح أو منتهي",
    backToLogin: "العودة لتسجيل الدخول",
    tabAudit: "السجل",
    auditActor: "المُنفّذ",
    auditAction: "الإجراء",
    auditResource: "المورد",
    auditSummary: "التفاصيل",
    auditWhen: "الوقت",
    auditIp: "IP",
    noAuditYet: "لا توجد إجراءات مُسجَّلة بعد.",
    filterAll: "الكل",
    action_create: "إنشاء",
    action_update: "تحديث",
    action_delete: "حذف",
    action_reorder: "إعادة ترتيب",
    action_upload: "رفع",
    action_request_reset: "طلب إعادة تعيين",
    action_reset_password: "إعادة تعيين كلمة المرور",
    resource_menu_item: "صنف",
    resource_category: "فئة",
    resource_user: "مستخدم",
    resource_settings: "إعدادات",
    resource_file: "ملف",
    // branch / order-type
    branchSelectTitle: "من أي فرع تطلب؟",
    branchSelectHint: "لعرض القائمة المناسبة، اختر نوع الفرع. يمكنك تغييره لاحقًا من الأعلى.",
    branchFull: "فرع كامل",
    branchFullHint: "جميع الأصناف متاحة — برجر، وجبات، سلطات وأكثر.",
    branchDriveThru: "درايف ثرو",
    branchDriveThruHint: "قائمة سريعة — مشروبات، سناكات وحلويات فقط.",
    branchChangeLabel: "الفرع",
    branchChange: "تغيير الفرع",
    kitchenRequired: "يتطلب مطبخ",
    kitchenRequiredHint: "مخفي في فروع درايف ثرو (يحتاج مطبخًا كاملًا).",
    driveThruBadge: "قائمة درايف ثرو",
    fullBadge: "القائمة الكاملة",
    tagKitchen: "مطبخ",
    // invites / email
    sendInviteEmail: "إرسال دعوة بالبريد",
    sendInviteHint: "سنرسل رابطًا لمرة واحدة لتعيين كلمة المرور. إذا لم يُهيّأ البريد، سيُطبع الرابط في سجل الخادم.",
    passwordOrInviteHint: "اتركه فارغًا لإرسال دعوة بالبريد",
    leaveBlankToInvite: "(فارغ = إرسال دعوة)",
    inviteSent: "تم إرسال الدعوة",
    inviteQueuedNoKey: "تم إنشاء المستخدم — اضبط إعدادات البريد على الخادم (SMTP_* أو RESEND_API_KEY) لإرسال الدعوة فعليًا",
    // QR posters
    qrPosterTitle: "ملصقات QR للفروع",
    qrPosterHint: "اطبعها في كل فرع لتوجيه الزبائن إلى القائمة الصحيحة.",
    qrDownloadFull: "تحميل ملصق الفرع الكامل",
    qrDownloadDrive: "تحميل ملصق درايف ثرو",
    qrScanCaption: "امسح للاطلاع على القائمة",
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("zed_lang") || "en");

  useEffect(() => {
    localStorage.setItem("zed_lang", lang);
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    document.body.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
  }, [lang]);

  const t = TRANSLATIONS[lang];
  const toggle = () => setLang((l) => (l === "en" ? "ar" : "en"));

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, isRtl: lang === "ar" }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
